import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-full border border-transparent font-semibold tracking-[-0.01em] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--surface-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-canvas)] disabled:pointer-events-none disabled:opacity-50',
          {
            'bg-primary text-white shadow-[var(--shadow-soft)] hover:-translate-y-px hover:bg-primary-light': variant === 'primary',
            'border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] text-[color:var(--ink-strong)] hover:-translate-y-px hover:border-[color:var(--surface-line-strong)] hover:bg-[var(--surface-panel)]': variant === 'secondary',
            'border-[color:var(--surface-line)] bg-transparent text-[color:var(--ink-base)] hover:border-[color:var(--surface-line-strong)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]': variant === 'outline',
            'text-[color:var(--ink-base)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]': variant === 'ghost',
            'bg-error text-white shadow-[var(--shadow-soft)] hover:-translate-y-px hover:brightness-110': variant === 'danger',
          },
          {
            'h-9 px-3.5 text-[0.82rem]': size === 'sm',
            'h-11 px-4.5 text-sm': size === 'md',
            'h-12 px-6 text-sm': size === 'lg',
          },
          className,
        )}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';
